import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { TimeUtils } from '../../../common/utilities/time-utils'
import { DateRangeWithAllChange, DateRangeWithAllPreset } from '../../interfaces/date-range-with-all-selector.models'

const PRESETS: DateRangeWithAllPreset[] = [
    'All',
    'Last 7 Days',
    'Last 30 Days',
    'Last 90 Days',
    'Weekly',
    'Monthly',
    'Yearly'
]

@Component({
    selector: 'fbr-date-range-with-all-selector',
    templateUrl: './date-range-with-all-selector.component.html',
    styleUrls: ['./date-range-with-all-selector.component.scss']
})
export class DateRangeWithAllSelectorComponent implements OnInit, OnChanges {
    @Input('start-year') startYear = 2019
    @Input('start-month') startMonth = 11
    @Input() initialPreset: DateRangeWithAllPreset = 'Last 7 Days'

    @Output('range-changed') rangeChanged = new EventEmitter<DateRangeWithAllChange>()

    readonly rangePresets = PRESETS

    selectedRange: DateRangeWithAllPreset = 'Last 7 Days'
    selectedYear = ''
    selectedMonth = ''
    selectedMonthWeek = ''
    years: { label: string; start: Date; end: Date }[] = []
    months: { label: string; start: Date; end: Date }[] = []
    monthWeeks: { label: string; start: Date; end: Date }[] = []
    start: Date = new Date()
    end: Date = new Date()

    ngOnInit(): void {
        this.selectedRange = this.initialPreset
        this.setSelects()
        this.updateStartEndFromSelection()
    }

    ngOnChanges(changes: SimpleChanges): void {
        const presetChange = changes.initialPreset
        if (presetChange && !presetChange.firstChange && presetChange.currentValue !== presetChange.previousValue) {
            this.selectedRange = this.initialPreset
            this.setSelects()
            this.updateStartEndFromSelection()
        }
    }

    get showRangeRefinementSelects(): boolean {
        return (
            this.selectedRange === 'Weekly' ||
            this.selectedRange === 'Monthly' ||
            this.selectedRange === 'Yearly'
        )
    }

    setRange(preset: DateRangeWithAllPreset): void {
        this.selectedRange = preset
        this.setSelects()
        this.updateStartEndFromSelection()
        this.rangeChanged.emit(this.buildPayload())
    }

    stateChanged(): void {
        this.setSelects()
        this.updateStartEndFromSelection()
        this.rangeChanged.emit(this.buildPayload())
    }

    private setSelects(): void {
        if (!this.showRangeRefinementSelects) {
            return
        }
        this.years = TimeUtils.generateYears(
            this.startYear,
            new Date().getFullYear(),
            this.selectedRange === 'Yearly'
        )
        this.selectedYear =
            this.selectedYear && this.years.filter(y => y.label.includes(this.selectedYear)).length
                ? this.selectedYear
                : this.years[this.years.length - 1].label
        const yearMatch = this.years.filter(y => y.label.includes(this.selectedYear))[0]
        const year = yearMatch ? yearMatch.start.getFullYear() : new Date().getFullYear()
        this.months = TimeUtils.generateMonths(year, this.startYear, this.startMonth, true, true)
        this.selectedMonth =
            this.selectedMonth && this.months.filter(m => m.label === this.selectedMonth).length
                ? this.selectedMonth
                : this.months[this.months.length - 1].label
        const monthRow = this.months.filter(m => m.label === this.selectedMonth)[0]
        const month = monthRow ? monthRow.start.getMonth() + 1 : new Date().getMonth() + 1
        this.monthWeeks = TimeUtils.generateWeeks(year, month)
        if (this.monthWeeks.length > 0) {
            this.selectedMonthWeek =
                this.monthWeeks.find(mw => mw.label === this.selectedMonthWeek)?.label ||
                this.monthWeeks[this.monthWeeks.length - 1].label
        }
    }

    private updateStartEndFromSelection(): void {
        if (this.selectedRange === 'All') {
            return
        }
        if (this.selectedRange === 'Yearly') {
            const year = this.years.filter(y => y.label.includes(this.selectedYear))[0]
            if (year) {
                this.start = year.start
                this.end = year.end
            }
            return
        }
        if (this.selectedRange === 'Monthly') {
            const month = this.months.filter(m => m.label === this.selectedMonth)[0]
            if (month) {
                this.start = month.start
                this.end = month.end
            }
            return
        }
        if (this.selectedRange === 'Weekly') {
            const week = this.monthWeeks.filter(w => w.label === this.selectedMonthWeek)[0]
            if (week) {
                this.start = week.start
                this.end = week.end
            }
            return
        }
        const end = new Date()
        const start = new Date(end)
        const daySpan =
            this.selectedRange === 'Last 7 Days' ? 7 : this.selectedRange === 'Last 30 Days' ? 30 : 90
        start.setDate(start.getDate() - daySpan)
        this.start = start
        this.end = end
    }

    private buildPayload(): DateRangeWithAllChange {
        if (this.selectedRange === 'All') {
            return { selectedRange: 'All' }
        }
        return {
            selectedRange: this.selectedRange,
            start: this.start,
            end: this.end
        }
    }
}
